/**
 * The effects layer.
 *
 * Everything here is PRESENTATION ONLY. Effects are drawn on a transparent
 * canvas sitting above the artwork; they never touch the document, never enter
 * history, and never appear in an export. That separation is what makes it
 * safe to be expressive: the picture the child saved is exactly the picture
 * they made, and the flourish that played when they lifted their finger is
 * gone the moment it finishes.
 *
 * The moments that get an effect are the moments something actually happened:
 *  - lifting off after a stroke      → the mark settles into the paper
 *  - a fill landing                  → the colour pours in from where you tapped
 *  - rubbing something out           → a puff, so the change is visible
 *  - finishing a picture             → one burst, once
 *
 * Nothing loops, nothing idles, nothing plays while the hand is moving. A
 * child's attention belongs on their drawing, not on the software.
 */

import { mix, rgba, rng, star, type BrushKind } from './media';
import type { StrokePoint } from './engine';

export interface Effect {
  /** ms since the effect started; the host advances this. */
  elapsed: number;
  duration: number;
  draw(ctx: CanvasRenderingContext2D, t: number): void;
}

/** Ease-out so everything decelerates into place rather than stopping dead. */
const out = (t: number) => 1 - Math.pow(1 - t, 3);
const outBack = (t: number) => 1 + 2.4 * Math.pow(t - 1, 3) + 1.6 * Math.pow(t - 1, 2);

/* ------------------------------------------------------------------ *
 * Lift-off — the moment a finger leaves the page
 * ------------------------------------------------------------------ */

/**
 * Each medium settles differently, because each real one does:
 * a marker pools a little extra ink where it stopped, watercolour keeps
 * spreading for a beat, glitter throws off a few sparks, spray drifts.
 */
export function liftOff(
  points: StrokePoint[],
  kind: BrushKind,
  color: string,
  size: number,
  seed: number,
): Effect {
  const last = points[points.length - 1];
  const prev = points[Math.max(0, points.length - 6)];
  const dx = last.x - prev.x;
  const dy = last.y - prev.y;
  const speed = Math.min(1, Math.hypot(dx, dy) / (size * 3));
  const dir = Math.atan2(dy, dx);

  switch (kind) {
    case 'watercolor':
      return bloom(last.x, last.y, color, size * 1.5);
    case 'glitter':
      return sparks(last.x, last.y, color, size, seed, 9);
    case 'spray':
      return drift(last.x, last.y, color, size, seed, dir, speed);
    case 'paint':
      return flick(last.x, last.y, color, size, dir, speed, seed);
    case 'rainbow':
      return sparks(last.x, last.y, 'rainbow', size, seed, 7);
    default:
      return pool(last.x, last.y, color, size, kind === 'pencil' || kind === 'crayon' ? 0.35 : 0.7);
  }
}

/** Marker / pencil / crayon: a small bloom of colour where the tip rested. */
function pool(x: number, y: number, color: string, size: number, strength: number): Effect {
  return {
    elapsed: 0,
    duration: 420,
    draw(ctx, t) {
      const e = out(t);
      const r = size * (0.25 + e * 0.55);
      ctx.save();
      ctx.globalAlpha = (1 - t) * strength * 0.55;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, rgba(color, 0.5));
      g.addColorStop(1, rgba(color, 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      // A hairline ring reads as the surface accepting the mark.
      ctx.globalAlpha = (1 - t) * 0.32;
      ctx.strokeStyle = rgba(color, 0.7);
      ctx.lineWidth = Math.max(1, size * 0.04);
      ctx.beginPath();
      ctx.arc(x, y, r * 0.92, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/** Watercolour: the pigment keeps creeping outward after the brush is gone. */
function bloom(x: number, y: number, color: string, size: number): Effect {
  return {
    elapsed: 0,
    duration: 900,
    draw(ctx, t) {
      const e = out(t);
      ctx.save();
      for (let ring = 0; ring < 3; ring++) {
        const r = size * (0.3 + e * (0.7 + ring * 0.35));
        ctx.globalAlpha = (1 - t) * (0.16 - ring * 0.04);
        const g = ctx.createRadialGradient(x, y, r * 0.5, x, y, r);
        g.addColorStop(0, rgba(color, 0));
        g.addColorStop(0.82, rgba(color, 0.55));
        g.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    },
  };
}

/** Glitter and rainbow: a handful of sparks that arc away and wink out. */
function sparks(
  x: number,
  y: number,
  color: string,
  size: number,
  seed: number,
  count: number,
): Effect {
  const rand = rng(seed ^ 0x9e37);
  const bits = Array.from({ length: count }, () => ({
    ang: rand() * Math.PI * 2,
    dist: size * (0.5 + rand() * 1.5),
    r: size * (0.07 + rand() * 0.1),
    delay: rand() * 0.25,
    hue: rand() * 360,
    spin: (rand() - 0.5) * 2,
  }));
  return {
    elapsed: 0,
    duration: 780,
    draw(ctx, t) {
      ctx.save();
      for (const b of bits) {
        const local = (t - b.delay) / (1 - b.delay);
        if (local <= 0) continue;
        const e = out(Math.min(1, local));
        const px = x + Math.cos(b.ang) * b.dist * e;
        const py = y + Math.sin(b.ang) * b.dist * e - size * 0.5 * e * e;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - local) * 0.95;
        ctx.fillStyle = color === 'rainbow' ? `hsl(${b.hue} 90% 62%)` : mix(color, '#FFFFFF', 0.65);
        ctx.translate(px, py);
        ctx.rotate(b.spin * e * 2);
        star(ctx, 0, 0, b.r * (1 - local * 0.4));
        ctx.restore();
      }
      ctx.restore();
    },
  };
}

/** Spray: the last of the mist keeps travelling and thins out. */
function drift(
  x: number,
  y: number,
  color: string,
  size: number,
  seed: number,
  dir: number,
  speed: number,
): Effect {
  const rand = rng(seed ^ 0x51ed);
  const motes = Array.from({ length: 22 }, () => ({
    ang: dir + (rand() - 0.5) * 1.4,
    dist: size * (0.2 + rand() * 0.9) * (0.4 + speed),
    r: size * (0.015 + rand() * 0.03),
    delay: rand() * 0.3,
  }));
  return {
    elapsed: 0,
    duration: 640,
    draw(ctx, t) {
      ctx.save();
      ctx.fillStyle = color;
      for (const m of motes) {
        const local = (t - m.delay) / (1 - m.delay);
        if (local <= 0) continue;
        const e = out(Math.min(1, local));
        ctx.globalAlpha = Math.max(0, 1 - local) * 0.5;
        ctx.beginPath();
        ctx.arc(x + Math.cos(m.ang) * m.dist * e, y + Math.sin(m.ang) * m.dist * e, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    },
  };
}

/** Paint: bristles release, so the stroke tapers and throws a fleck or two. */
function flick(
  x: number,
  y: number,
  color: string,
  size: number,
  dir: number,
  speed: number,
  seed: number,
): Effect {
  const rand = rng(seed ^ 0x2f1b);
  const flecks = Array.from({ length: 5 }, () => ({
    ang: dir + (rand() - 0.5) * 0.9,
    dist: size * (0.4 + rand() * 1.1) * (0.3 + speed),
    r: size * (0.05 + rand() * 0.07),
  }));
  return {
    elapsed: 0,
    duration: 520,
    draw(ctx, t) {
      const e = out(t);
      ctx.save();
      ctx.fillStyle = color;
      ctx.globalAlpha = (1 - t) * 0.55;
      for (const f of flecks) {
        ctx.beginPath();
        ctx.ellipse(
          x + Math.cos(f.ang) * f.dist * e,
          y + Math.sin(f.ang) * f.dist * e,
          f.r * (1 - t * 0.5),
          f.r * 0.6 * (1 - t * 0.5),
          f.ang,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      ctx.restore();
    },
  };
}

/* ------------------------------------------------------------------ *
 * Fill — the colour pours in
 * ------------------------------------------------------------------ */

/**
 * The engine fills the region instantly, because the document must be true
 * the moment the operation is recorded. This effect then covers the region
 * with what was there BEFORE and wipes that cover away from the tap point, so
 * what the child sees is their colour flooding outward from their own finger.
 *
 * `radius` is how far the wipe has to travel to clear the region.
 */
export function pour(
  path: Path2D,
  previous: string | null,
  next: string,
  x: number,
  y: number,
  radius: number,
): Effect {
  return {
    elapsed: 0,
    duration: 460,
    draw(ctx, t) {
      const e = out(t);
      ctx.save();
      ctx.clip(path);
      if (t < 1) {
        // Whatever the region used to look like, held back from the wipe.
        ctx.fillStyle = previous ?? '#FFFFFF';
        ctx.globalAlpha = previous ? 1 : 0.96;
        const box = radius * 2.4;
        ctx.fillRect(x - box, y - box, box * 2, box * 2);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.globalAlpha = 1;
        const g = ctx.createRadialGradient(x, y, 0, x, y, Math.max(1, radius * e * 1.06));
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(0.86, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(1, radius * e * 1.06), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
      }
      // A soft leading edge, so the pour has a wavefront rather than a hard rim.
      const lead = radius * e;
      ctx.globalAlpha = (1 - t) * 0.5;
      ctx.strokeStyle = mix(next, '#FFFFFF', 0.6);
      ctx.lineWidth = Math.max(2, radius * 0.05);
      ctx.beginPath();
      ctx.arc(x, y, lead, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/** A ring at the tap point, so a fill on an already-filled region still reads. */
export function tapRing(x: number, y: number, color: string, size: number): Effect {
  return {
    elapsed: 0,
    duration: 380,
    draw(ctx, t) {
      const e = outBack(t);
      ctx.save();
      ctx.globalAlpha = (1 - t) * 0.7;
      ctx.strokeStyle = mix(color, '#FFFFFF', 0.4);
      ctx.lineWidth = Math.max(2, size * 0.06) * (1 - t);
      ctx.beginPath();
      ctx.arc(x, y, size * 0.3 + size * 0.9 * e, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/** Erase: a chalky puff, so rubbing out is something you can see happen. */
export function puff(x: number, y: number, size: number, seed: number): Effect {
  const rand = rng(seed ^ 0x77aa);
  const bits = Array.from({ length: 8 }, () => ({
    ang: rand() * Math.PI * 2,
    dist: size * (0.3 + rand() * 0.8),
    r: size * (0.12 + rand() * 0.16),
  }));
  return {
    elapsed: 0,
    duration: 500,
    draw(ctx, t) {
      const e = out(t);
      ctx.save();
      ctx.fillStyle = '#B9AEC6';
      for (const b of bits) {
        ctx.globalAlpha = (1 - t) * 0.35;
        ctx.beginPath();
        ctx.arc(
          x + Math.cos(b.ang) * b.dist * e,
          y + Math.sin(b.ang) * b.dist * e,
          b.r * (0.5 + e),
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      ctx.restore();
    },
  };
}

/** One celebration, at the one moment a picture is finished. */
export function finishBurst(w: number, h: number, seed: number): Effect {
  const rand = rng(seed);
  const bits = Array.from({ length: 26 }, () => ({
    x: rand() * w,
    y: rand() * h,
    r: Math.min(w, h) * (0.012 + rand() * 0.026),
    delay: rand() * 0.5,
    hue: rand() * 360,
  }));
  return {
    elapsed: 0,
    duration: 1100,
    draw(ctx, t) {
      ctx.save();
      for (const b of bits) {
        const local = (t - b.delay) / (1 - b.delay);
        if (local <= 0) continue;
        const e = outBack(Math.min(1, local));
        ctx.globalAlpha = Math.max(0, 1 - local);
        ctx.fillStyle = `hsl(${b.hue} 88% 64%)`;
        star(ctx, b.x, b.y - local * h * 0.06, b.r * e);
      }
      ctx.restore();
    },
  };
}

/* ------------------------------------------------------------------ *
 * The player
 * ------------------------------------------------------------------ */

/**
 * Runs effects on their own canvas.
 *
 * It only animates while something is playing, so an idle studio costs no
 * frames — which matters on the older iPads this has to run on.
 */
export class EffectPlayer {
  private live: Effect[] = [];
  private raf = 0;
  private last = 0;
  private canvas: HTMLCanvasElement | null = null;
  private width: number;
  private height: number;
  /** Art-space size, so effects share coordinates with the artwork. */
  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  attach(canvas: HTMLCanvasElement | null) {
    this.canvas = canvas;
  }

  play(effect: Effect) {
    this.live.push(effect);
    this.start();
  }

  get busy() {
    return this.live.length > 0;
  }

  private start() {
    if (this.raf) return;
    this.last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(64, now - this.last);
      this.last = now;
      for (const e of this.live) e.elapsed += dt;
      this.live = this.live.filter((e) => e.elapsed < e.duration);
      this.render();
      if (this.live.length > 0) {
        this.raf = requestAnimationFrame(tick);
      } else {
        this.raf = 0;
        this.render(); // one final clear
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  private render() {
    const canvas = this.canvas;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    if (cssW === 0 || cssH === 0) return;
    const tw = Math.round(cssW * dpr);
    const th = Math.round(cssH * dpr);
    if (canvas.width !== tw || canvas.height !== th) {
      canvas.width = tw;
      canvas.height = th;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (this.live.length === 0) return;
    const scale = (cssW / this.width) * dpr;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    for (const e of this.live) {
      const t = Math.min(1, e.elapsed / e.duration);
      ctx.save();
      e.draw(ctx, t);
      ctx.restore();
    }
  }

  destroy() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.live = [];
  }
}
